// @react
import React, { ReactElement } from 'react'
// @libraries
import { Divider } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import strings from 'lib/constants/strings'
import { ExternalRouteNames } from 'constants/routeNames'
// @components
import CheckList from '@bit/atd.web.checklist'
import ProgressBar from '@bit/atd.web.progress-bar'
import Box from '@bit/atd.web.box'
import icons from '@bit/atd.web.icons/dist/getIcons'

const titleBlockStyles = makeStyles({
  title: {
    color: '#424140',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '18px',
    marginBottom: '8px',
  },
  description: {
    color: '#424140',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
  },
})

const capabilityStrings = strings.DASHBOARD.CAPABILITY
type NoStartedPlanProps = {
  pathLabel: string
}
type Domain = {
  domainTitle: string
  totalProgress: number
}
type Domains = {
  domains?: Domain[]
}

type CheckListItem = {
  label?: string
  glyph?: keyof typeof icons
  checked: boolean
}

interface TitleBlockProps {
  title: string
  btnText: string
  btnLink: string
  description?: string | React.ReactElement
  children?: React.ReactElement
}
interface PartialPathProps {
  progress: number
  total: number
  pathLabel: string
}

/**
 *
 * @param title
 * @param description
 * @param children
 * @param btnText
 * @param btnLink
 * @constructor
 */
const TitleBlock = ({
  title,
  description,
  children,
  btnText,
  btnLink,
}: TitleBlockProps): ReactElement => {
  const classes = titleBlockStyles()

  return (
    <Box
      variant={'reduced'}
      footer={{
        alignment: 'stretch',
        link: {
          url: btnLink,
          label: btnText,
          variant: 'primary',
        },
      }}
    >
      <>
        <Typography className={classes.title} component={'p'}>
          {title}
        </Typography>
        {description && (
          <Typography className={classes.description} component={'p'}>
            {description}
          </Typography>
        )}
        {children}
      </>
    </Box>
  )
}

/**
 *
 * @constructor
 */
export const NoAssessment = (): ReactElement => {
  return (
    <TitleBlock
      title={capabilityStrings.CONTENT.TAKE_ASSESSMENT}
      description={capabilityStrings.CONTENT.TAKE_ASSESSMENT_DESCRIPTION}
      btnText={capabilityStrings.LABELS.BEGIN_ASSESSMENT}
      btnLink={ExternalRouteNames.CM_ASSESSMENT_PERSONAL_COMMUNICATION}
    />
  )
}

/**
 *
 * @param domains
 */
const getCheckList = (domains: Domains): CheckListItem[] | undefined => {
  if (!domains.domains?.length) {
    return undefined
  }

  return domains.domains?.map((item) => {
    return {
      label: labelAssigner(item.domainTitle),
      glyph: iconAssigner(item.totalProgress),
      checked: !!item?.totalProgress,
    }
  })
}

/**
 *
 * @param progress
 */
const iconAssigner = (progress: number) => {
  if (!progress || progress === 0) return
  if (progress > 0 && progress < 100) return 'in-progress'
  if (progress === 100) return 'checkmark'
}

/**
 *
 * @param title
 */
const labelAssigner = (title: string): string => {
  if (!title) return ''
  if (title === 'Personal') return capabilityStrings.CONTENT.PERSONAL
  if (title === 'Professional') return capabilityStrings.CONTENT.PROFESSIONAL
  if (title === 'Organizational')
    return capabilityStrings.CONTENT.ORGANIZATIONAL
  return ''
}

/**
 *
 * @constructor
 * @param domains
 */
export const PartialAssessment = (domains: Domains): ReactElement => {
  return (
    <TitleBlock
      title={capabilityStrings.CONTENT.FINISH_ASSESSMENT}
      btnText={capabilityStrings.LABELS.FINISH_ASSESSMENT}
      btnLink={ExternalRouteNames.CM_ASSESSMENT_PERSONAL_COMMUNICATION}
    >
      <CheckList list={getCheckList(domains)} />
    </TitleBlock>
  )
}

/**
 *
 * @constructor
 * @param domains
 */

export const CompleteAssessment = (domains: Domains): ReactElement => {
  return (
    <TitleBlock
      title={capabilityStrings.CONTENT.COMPLETE_ASSESSMENT}
      btnText={capabilityStrings.LABELS.REASSESS}
      btnLink={ExternalRouteNames.CM_ASSESSMENT_PERSONAL_COMMUNICATION}
    >
      <div style={{ marginTop: '4px' }}>
        <CheckList list={getCheckList(domains)} />
      </div>
    </TitleBlock>
  )
}
/**
 *
 * @param pathLabel
 * @constructor
 */
export const NoStartedPlan = ({
  pathLabel,
}: NoStartedPlanProps): ReactElement => {
  const classes = titleBlockStyles()

  return (
    <TitleBlock
      title={capabilityStrings.CONTENT.LEARNING_PATH}
      description={pathLabel}
      btnText={capabilityStrings.LABELS.GET_STARTED}
      btnLink={ExternalRouteNames.CM_LEARNING_PLANS_ALL}
    >
      <>
        <div style={{ padding: '16px 0' }}>
          <Divider />
        </div>
        <div>
          <Typography className={classes.title} component={'p'}>
            {capabilityStrings.CONTENT.LEARNING_PLAN}
          </Typography>
          <Typography className={classes.description} component={'p'}>
            {capabilityStrings.CONTENT.LEARNING_PLAN_DESCRIPTION}
          </Typography>
        </div>
      </>
    </TitleBlock>
  )
}
/**
 *
 * @constructor
 */
export const NoStartedPath = (): ReactElement => {
  return (
    <TitleBlock
      title={capabilityStrings.CONTENT.CHOOSE_PATH}
      description={capabilityStrings.CONTENT.CHOOSE_PATH_DESCRIPTION}
      btnText={capabilityStrings.LABELS.BROWSE_PATHS}
      btnLink={ExternalRouteNames.CM_LEARNING_PLANS_GOAL_SELF_GUIDED}
    />
  )
}

/**
 *
 * @param progress
 * @param total
 * @param pathLabel
 * @constructor
 */
export const PartialPath = ({
  progress,
  total,
  pathLabel,
}: PartialPathProps): ReactElement => {
  const classes = titleBlockStyles()

  return (
    <TitleBlock
      title={capabilityStrings.CONTENT.LEARNING_PATH}
      description={pathLabel}
      btnText={capabilityStrings.LABELS.VIEW_ALL}
      btnLink={ExternalRouteNames.CM_LEARNING_PLANS_ALL}
    >
      <>
        <div style={{ padding: '16px 0' }}>
          <Divider />
        </div>
        <div>
          <Typography className={classes.title} component={'p'}>
            {capabilityStrings.CONTENT.LEARNING_PLAN}
          </Typography>
          <ProgressBar
            count={progress}
            total={total}
            label={capabilityStrings.CONTENT.ACTIVITIES_COMPLETED}
          />
        </div>
      </>
    </TitleBlock>
  )
}

/**
 *
 * @constructor
 */
export const CompletePath = (): ReactElement => {
  return (
    <TitleBlock
      title={capabilityStrings.CONTENT.CONGRATULATIONS}
      description={capabilityStrings.CONTENT.CONGRATULATIONS_DESCRIPTION}
      btnText={capabilityStrings.LABELS.EXPLORE_PATHS}
      btnLink={ExternalRouteNames.CM_LEARNING_PLANS_GOAL_SELF_GUIDED}
    />
  )
}
